import {Box, Button, ButtonGroup} from '@mui/material';
import {t} from 'i18next';
import update from 'immutability-helper';
import {isEmpty} from 'lodash';
import {useEffect, useMemo, useState} from 'react';

import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {
  usePanel,
  usePanelFilter,
} from '../../dashboards/entities/DashboardEntityContext';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';
import CheckoutStationDetailsReportChart, {
  CheckoutStationDetailsReportChartData,
  getCheckoutStationDetailsReportChartData,
} from './CheckoutStationDetailsReportChart';
import CheckoutStationDetailsReportGrid, {
  CheckoutStationDetailsReportGridData,
  getCheckoutStationDetailsReportGridData,
} from './CheckoutStationDetailsReportGrid';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value: DashboardPanelData) => void;
}

export interface CheckoutStationDetailsReportData {
  viewType: 'grid' | 'chart';
  grid: CheckoutStationDetailsReportGridData;
  chart: CheckoutStationDetailsReportChartData;
  detachedFromDashboardDate?: boolean;
}

const getCheckoutStationDetailsReportData =
  (): CheckoutStationDetailsReportData => ({
    viewType: 'grid',
    grid: getCheckoutStationDetailsReportGridData(),
    chart: getCheckoutStationDetailsReportChartData(),
  });

export const CheckoutStationDetailsReport = (props: Props) => {
  const [panel] = usePanel();
  const [config, setConfig] = useState<CheckoutStationDetailsReportData>(
    !isEmpty(props.value)
      ? (props.value as CheckoutStationDetailsReportData)
      : getCheckoutStationDetailsReportData()
  );

  useEffect(() => {
    props.onUpdate?.({...config});
  }, [config]);

  // Sync the detach flag when Apply with "re-attach" clears it from outside.
  useEffect(() => {
    setConfig((prev) => ({
      ...prev,
      detachedFromDashboardDate: !!props.value?.detachedFromDashboardDate,
    }));
  }, [props.value?.detachedFromDashboardDate]);

  const {filter} = usePanelFilter();
  const dashboardDates =
    !config.detachedFromDashboardDate && filter?.params?.date_start
      ? {
          date_start: filter.params.date_start as string,
          date_end: filter.params.date_end as string,
        }
      : null;

  const gridValue = useMemo<CheckoutStationDetailsReportGridData>(() => {
    const base = config.grid;
    if (!dashboardDates) {
      return base;
    }
    if (
      base.params?.date_start === dashboardDates.date_start &&
      base.params?.date_end === dashboardDates.date_end
    ) {
      return base;
    }
    return {...base, params: {...base.params, ...dashboardDates}};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.grid, dashboardDates?.date_start, dashboardDates?.date_end]);

  const chartValue = useMemo<CheckoutStationDetailsReportChartData>(() => {
    const base = config.chart;
    if (!dashboardDates) {
      return base;
    }
    if (
      base.params?.date_start === dashboardDates.date_start &&
      base.params?.date_end === dashboardDates.date_end
    ) {
      return base;
    }
    return {...base, params: {...base.params, ...dashboardDates}};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.chart, dashboardDates?.date_start, dashboardDates?.date_end]);

  return (
    <>
      <DashboardPanelTitleSlot>
        {t(`panels.${panel?.code}`)}
      </DashboardPanelTitleSlot>
      <Box position="absolute" top={8} left={8}>
        <ButtonGroup>
          <Button
            size="small"
            variant={config.viewType === 'grid' ? 'contained' : 'outlined'}
            onClick={() => {
              setConfig(
                update(config, {
                  viewType: {
                    $set: 'grid',
                  },
                })
              );
            }}
          >
            Grid
          </Button>

          <Button
            size="small"
            variant={config.viewType === 'chart' ? 'contained' : 'outlined'}
            onClick={() => {
              setConfig(
                update(config, {
                  viewType: {
                    $set: 'chart',
                  },
                })
              );
            }}
          >
            Graph
          </Button>
        </ButtonGroup>
      </Box>

      {config.viewType === 'grid' ? (
        <CheckoutStationDetailsReportGrid
          value={gridValue}
          onChange={(v) => {
            const dateChanged =
              dashboardDates &&
              (v.params?.date_start !== dashboardDates.date_start ||
                v.params?.date_end !== dashboardDates.date_end);
            setConfig(
              update(
                dateChanged
                  ? {...config, detachedFromDashboardDate: true}
                  : config,
                {grid: {$set: v}}
              )
            );
          }}
        />
      ) : config.viewType === 'chart' ? (
        <CheckoutStationDetailsReportChart
          value={chartValue}
          onChange={(v) => {
            const dateChanged =
              dashboardDates &&
              (v.params?.date_start !== dashboardDates.date_start ||
                v.params?.date_end !== dashboardDates.date_end);
            setConfig(
              update(
                dateChanged
                  ? {...config, detachedFromDashboardDate: true}
                  : config,
                {chart: {$set: v}}
              )
            );
          }}
        />
      ) : null}
    </>
  );
};
